import { useState, type ReactNode } from "react";
import type { Clarification, Critique, Idea, Persona } from "../data/types";
import {
  clarificationsOf,
  critiquesOf,
  ideaStateLabel,
  latestBuildSpec,
  personasOf,
  scanResultOf,
} from "../data/types";
import { copyIdeaAsMarkdown, downloadIdeaAsMarkdown } from "../util/livingDoc";

interface Props {
  idea: Idea;
  onBack: () => void;
  onDelete: () => void;
  onClarify: () => void;
  onScan: () => void;
  onPersonas: () => void;
  onBuildSpec: () => void;
}

export function IdeaDetailScreen(props: Props) {
  const { idea } = props;
  const [showConfirm, setShowConfirm] = useState(false);
  const [copied, setCopied] = useState(false);

  const clarifications = clarificationsOf(idea);
  const scan = scanResultOf(idea);
  const personas = personasOf(idea);
  const critiques = critiquesOf(idea);
  const spec = latestBuildSpec(idea);

  const handleCopyAll = async () => {
    const ok = await copyIdeaAsMarkdown(idea);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <>
      <header className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-black/5 bg-bg/90 px-4 py-3 backdrop-blur">
        <button className="btn-ghost" onClick={props.onBack}>
          Back
        </button>
        <h1 className="text-lg font-semibold tracking-tight">Idea</h1>
        <button className="btn-danger" onClick={() => setShowConfirm(true)}>
          Delete
        </button>
      </header>

      <main className="flex flex-1 flex-col gap-5 px-4 py-4">
        <section className="flex flex-col gap-2">
          <h2 className="text-2xl font-semibold tracking-tight">{idea.title}</h2>
          <div className="inline-flex w-fit items-center rounded-full bg-accent-soft px-2 py-0.5 text-xs font-medium text-accent">
            {ideaStateLabel(idea.state)}
          </div>
        </section>

        <Section title="Raw capture">
          <p className="whitespace-pre-wrap text-sm text-ink-muted">
            {idea.rawCapture.length > 0 ? idea.rawCapture : "No additional notes."}
          </p>
        </Section>

        <ClarifySection
          clarifications={clarifications}
          onClarify={props.onClarify}
        />

        <ScanSection
          hasScan={Boolean(scan)}
          productCount={scan?.products.length ?? 0}
          onScan={props.onScan}
        />

        <PersonasSection
          personas={personas}
          critiques={critiques}
          onOpen={props.onPersonas}
        />

        <BuildSpecSection
          hasSpec={Boolean(spec)}
          generatedAtMs={spec?.generatedAtMs}
          onOpen={props.onBuildSpec}
        />

        <section className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold">Living doc export</h3>
          <div className="card flex flex-col gap-3 p-4">
            <p className="text-sm text-ink-muted">
              Export everything captured for this idea — raw capture, clarifications, scan,
              personas + critique, and the latest build spec — as a single Markdown document.
            </p>
            <div className="flex flex-wrap gap-2">
              <button className="btn-ghost" onClick={handleCopyAll}>
                {copied ? "Copied ✓" : "Copy living doc"}
              </button>
              <button className="btn-ghost" onClick={() => downloadIdeaAsMarkdown(idea)}>
                Download .md
              </button>
            </div>
          </div>
        </section>
      </main>

      {showConfirm && (
        <ConfirmDialog
          title="Delete idea?"
          body={`This permanently removes "${idea.title}" from this device.`}
          confirmLabel="Delete"
          onCancel={() => setShowConfirm(false)}
          onConfirm={() => {
            setShowConfirm(false);
            props.onDelete();
          }}
        />
      )}
    </>
  );
}

function ClarifySection(props: {
  clarifications: Clarification[];
  onClarify: () => void;
}) {
  const count = props.clarifications.length;
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-sm font-semibold">Mom-Test clarification</h3>
      <div className="card flex flex-col gap-3 p-4">
        {count === 0 ? (
          <>
            <p className="text-sm">
              5–8 questions probing past behaviour, current alternatives, and dealbreakers — the
              Mom-Test discipline from Rob Fitzpatrick.
            </p>
            <button className="btn-primary self-start" onClick={props.onClarify}>
              Start clarifying
            </button>
          </>
        ) : (
          <>
            <p className="text-sm text-ink-muted">
              {count} {count === 1 ? "answer" : "answers"} saved.
            </p>
            <ul className="flex flex-col gap-2">
              {props.clarifications.map((c, idx) => (
                <li key={`${c.timestampMs}-${idx}`} className="rounded-xl bg-bg p-3">
                  <div className="text-xs font-semibold text-ink-muted">Q{idx + 1}</div>
                  <div className="text-sm">{c.question}</div>
                  <div className="mt-2 text-xs font-semibold text-ink-muted">A{idx + 1}</div>
                  <div className="whitespace-pre-wrap text-sm">{c.answer}</div>
                </li>
              ))}
            </ul>
            <button className="btn-ghost self-start" onClick={props.onClarify}>
              Continue clarifying
            </button>
          </>
        )}
      </div>
    </section>
  );
}

function ScanSection(props: {
  hasScan: boolean;
  productCount: number;
  onScan: () => void;
}) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-sm font-semibold">Market scan</h3>
      <div className="card flex flex-col gap-3 p-4">
        <p className="text-sm">
          Brainstorm similar / competing products to ground the personas. (v2 plan: real Product
          Hunt API.)
        </p>
        {props.hasScan ? (
          <>
            <p className="text-sm text-ink-muted">
              {props.productCount} products found.
            </p>
            <button className="btn-ghost self-start" onClick={props.onScan}>
              Open scan
            </button>
          </>
        ) : (
          <button className="btn-primary self-start" onClick={props.onScan}>
            Run scan
          </button>
        )}
      </div>
    </section>
  );
}

function PersonasSection(props: {
  personas: Persona[];
  critiques: Critique[];
  onOpen: () => void;
}) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-sm font-semibold">Personas + critique</h3>
      <div className="card flex flex-col gap-3 p-4">
        <p className="text-sm">
          5 personas grounded in the scan and clarifications, each reacting in their own voice.
        </p>
        {props.personas.length === 0 ? (
          <button className="btn-primary self-start" onClick={props.onOpen}>
            Generate personas
          </button>
        ) : (
          <>
            <ul className="flex flex-wrap gap-2">
              {props.personas.map((p) => {
                const c = props.critiques.find((cr) => cr.personaId === p.id);
                return (
                  <li
                    key={p.id}
                    className="rounded-full bg-bg px-2 py-1 text-xs ring-1 ring-black/5"
                  >
                    <span className="font-semibold">{p.name}</span>
                    {c && (
                      <span className="ml-1 text-ink-muted">
                        · fit {c.problemFitScore}/5 · switch {c.switchLikelihoodScore}/5
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
            <button className="btn-ghost self-start" onClick={props.onOpen}>
              Open personas
              {props.critiques.length > 0 && props.critiques.length < props.personas.length
                ? ` (${props.critiques.length}/${props.personas.length} critiqued)`
                : ""}
            </button>
          </>
        )}
      </div>
    </section>
  );
}

function BuildSpecSection(props: {
  hasSpec: boolean;
  generatedAtMs?: number;
  onOpen: () => void;
}) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-sm font-semibold">Build spec</h3>
      <div className="card flex flex-col gap-3 p-4">
        <p className="text-sm">
          Paste-ready Markdown spec for Cursor / Claude Code / Lovable, with persona-evidence tags
          on every feature and non-goal.
        </p>
        {props.hasSpec ? (
          <>
            <p className="text-sm text-ink-muted">
              Last generated{" "}
              {props.generatedAtMs
                ? new Date(props.generatedAtMs).toLocaleString()
                : "unknown"}
              . Copy or download from the spec screen.
            </p>
            <button className="btn-ghost self-start" onClick={props.onOpen}>
              Open spec
            </button>
          </>
        ) : (
          <button className="btn-primary self-start" onClick={props.onOpen}>
            Generate build spec
          </button>
        )}
      </div>
    </section>
  );
}

function Section(props: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-sm font-semibold">{props.title}</h3>
      <div className="card p-4">{props.children}</div>
    </section>
  );
}

function ConfirmDialog(props: {
  title: string;
  body: string;
  confirmLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-20 flex items-end justify-center bg-black/40 p-4 sm:items-center"
      onClick={props.onCancel}
    >
      <div
        className="w-full max-w-sm rounded-2xl bg-surface p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-semibold">{props.title}</h3>
        <p className="mt-2 text-sm text-ink-muted">{props.body}</p>
        <div className="mt-4 flex justify-end gap-2">
          <button className="btn-ghost" onClick={props.onCancel}>
            Cancel
          </button>
          <button className="btn-danger" onClick={props.onConfirm}>
            {props.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
